// Plain-English names for AWS services, for the management Summary page.
//
// AWS bills by service ("Amazon Relational Database Service", "EC2 - Other").
// Those names mean nothing to most readers of a monthly cost summary, so the
// Summary groups them into a handful of categories and gives each service a
// short everyday name. Matching is by keyword, so new or renamed AWS services
// still land somewhere sensible; anything unrecognised goes to "Other".

export const CATEGORIES = [
  { id: "databases", label: "Databases", note: "Stored business data: RDS, DynamoDB, ElastiCache…" },
  { id: "compute", label: "Servers", note: "Virtual servers, containers and functions that run the apps" },
  { id: "storage", label: "Storage & backup", note: "Files, disks, snapshots and backups" },
  { id: "network", label: "Networking", note: "Private networks, load balancers, content delivery, data transfer" },
  { id: "security", label: "Security", note: "Firewalls, encryption keys, threat detection, certificates" },
  { id: "monitoring", label: "Monitoring & management", note: "Logs, metrics, audit trails, configuration" },
  { id: "messaging", label: "Email & messaging", note: "Email sending, queues, notifications" },
  { id: "support", label: "AWS support plan", note: "The support subscription" },
  { id: "tax", label: "Tax", note: "Tax AWS adds to the bill" },
  { id: "other", label: "Other services", note: "Everything not covered above" },
];

// First match wins, so the more specific patterns come first.
const RULES = [
  [/^tax$/i, "tax"],
  [/support/i, "support"],
  [/ebs|elastic block store|snapshot/i, "storage"],
  [/ec2 - other/i, "storage"], // mostly EBS volumes and snapshots; NAT/transfer are a smaller share
  [
    /relational database|rds|aurora|dynamodb|elasticache|documentdb|redshift|memorydb|neptune|keyspaces|timestream|opensearch|database/i,
    "databases",
  ],
  [
    /elastic compute cloud|ec2|lambda|elastic container|ecs|eks|kubernetes|fargate|lightsail|batch|app runner|elastic beanstalk|container registry|ecr/i,
    "compute",
  ],
  [/simple storage service|s3|glacier|elastic file system|efs|fsx|backup|storage gateway/i, "storage"],
  [
    /virtual private cloud|vpc|cloudfront|load balancing|route 53|direct connect|api gateway|global accelerator|transit gateway|data transfer|vpn|privatelink/i,
    "network",
  ],
  [
    /waf|shield|key management|kms|guardduty|security hub|secrets manager|certificate manager|inspector|firewall|macie|cloudhsm|cognito|detective|iam access/i,
    "security",
  ],
  [
    /cloudwatch|cloudtrail|config|systems manager|x-ray|cost explorer|trusted advisor|control tower|organizations|service catalog|cloudformation/i,
    "monitoring",
  ],
  [
    /simple email|ses|simple notification|sns|simple queue|sqs|eventbridge|pinpoint|step functions|\bmq\b/i,
    "messaging",
  ],
];

export function categoryOf(service) {
  for (const [re, id] of RULES) if (re.test(service || "")) return id;
  return "other";
}

const SHORT = {
  "Amazon Relational Database Service": "Databases (RDS)",
  "Amazon Elastic Compute Cloud - Compute": "Servers (EC2)",
  "EC2 - Other": "Server disks & snapshots",
  "Amazon Virtual Private Cloud": "Private networking (VPC)",
  "Amazon Simple Storage Service": "File storage (S3)",
  "Amazon CloudFront": "Content delivery (CloudFront)",
  "AWS Key Management Service": "Encryption keys (KMS)",
  "Amazon CloudWatch": "Monitoring (CloudWatch)",
  AmazonCloudWatch: "Monitoring (CloudWatch)", // Cost Explorer's own spelling, no space
  "AWS WAF": "Web firewall (WAF)",
  "Elastic Load Balancing": "Load balancers",
  "Amazon Elastic Load Balancing": "Load balancers",
  "AWS Lambda": "Functions (Lambda)",
  "Amazon Elastic Container Service": "Containers (ECS)",
  "Amazon Simple Email Service": "Email sending (SES)",
  "Amazon Route 53": "Domain names (Route 53)",
  "AWS Backup": "Backups",
  "Amazon DynamoDB": "Databases (DynamoDB)",
  "Amazon ElastiCache": "In-memory cache (ElastiCache)",
  "AWS Secrets Manager": "Secrets storage",
  "Amazon GuardDuty": "Threat detection (GuardDuty)",
  "AWS CloudTrail": "Audit trail (CloudTrail)",
  "AWS Config": "Configuration tracking (Config)",
  Tax: "Tax",
};

/** "Amazon Relational Database Service" → "Databases (RDS)"; unknown names lose their "Amazon"/"AWS" prefix. */
export function friendlyService(service) {
  if (SHORT[service]) return SHORT[service];
  return (service || "").replace(/^(Amazon|AWS)\s+/i, "");
}
